import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, mediaSrc, formatFCFA } from "@/lib/api";
import { BadgeCheck, MapPin, Share2, Star, MessageCircle, Eye, ShieldQuestion, Heart, Truck, Package, Clock, Store } from "lucide-react";
import ProductCard from "@/components/Product/ProductCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ShopPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    api.get(`/shops/${id}`).then(r => setShop(r.data));
    api.get(`/products/shop/${id}`).then(r => setProducts(r.data || []));
    api.get(`/shops/${id}/reviews`).then(r => setReviews(r.data || []));
    api.post(`/shops/${id}/click`).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!user) { setIsFav(false); return; }
    api.get("/favorites/ids").then(r => setIsFav((r.data?.shop_ids || []).includes(id))).catch(() => {});
  }, [id, user]);

  if (!shop) return <div className="p-8 text-center text-[#73706B]">Chargement…</div>;

  const toggleFav = async () => {
    if (!user) { toast.error("Connectez-vous pour suivre cette boutique"); nav("/connexion"); return; }
    try {
      const { data } = await api.post("/favorites/toggle", { target_id: id, target_type: "shop" });
      setIsFav(data.favorited);
      toast.success(data.favorited ? "Boutique ajoutée aux favoris" : "Retirée des favoris");
    } catch {}
  };

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: shop.name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); }
    } catch {}
  };

  const onWhatsApp = (e) => {
    api.post(`/events/contact`, { channel: "whatsapp", shop_id: shop.id }).catch(() => {});
  };
  const waDigits = (shop?.whatsapp || "").replace(/\D/g, "");
  const waText = encodeURIComponent(`Bonjour, je viens de découvrir votre boutique « ${shop?.name || ""} » sur Forum CI.`);
  const waHref = `https://wa.me/${waDigits}?text=${waText}`;

  return (
    <div className="pb-8">
      {/* Cover — fixed height, separate block (no overlap) */}
      <div className="relative h-36 sm:h-52 bg-[#F3F1EC] overflow-hidden">
        {shop.cover_image ? (
          <img src={mediaSrc(shop.cover_image)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-[#FFF3E0] to-[#FCFCF9]" />
        )}
      </div>

      <div className="max-w-5xl mx-auto px-3 sm:px-6">
        {/* Header card — sits below cover with profile image properly framed */}
        <div className="relative rounded-2xl bg-white dark:bg-[#1A1918] border border-[#E8E6E1] -mt-12 sm:-mt-14 p-4 sm:p-5 flex items-start gap-4 shadow-sm">
          <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-2 border-white dark:border-[#2E2C29] bg-[#F3F1EC] overflow-hidden shadow-md">
            {shop.profile_image ? (
              <img src={mediaSrc(shop.profile_image)} alt={shop.name} className="w-full h-full object-cover object-center" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-extrabold text-2xl text-[#73706B] bg-[#F3F1EC]">
                {shop.name?.[0]?.toUpperCase()}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h1 className="font-[Manrope] font-bold text-xl sm:text-2xl truncate text-[#1A1918]">{shop.name}</h1>
              {shop.verified ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#E6F4EA] text-[#0A7D3A]" data-testid="shop-verified">
                  <BadgeCheck className="h-3.5 w-3.5" /> Boutique vérifiée
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#F3F1EC] text-[#73706B]" data-testid="shop-unverified">
                  <ShieldQuestion className="h-3.5 w-3.5" /> Non vérifiée
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-[#73706B] mt-2 flex-wrap">
              {shop.city && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" />{shop.city}{shop.market ? ` · ${shop.market}` : ""}
                </span>
              )}
              {shop.rating_count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Star className="h-3 w-3 fill-[#E65100] text-[#E65100]" />
                  {shop.rating_avg.toFixed(1)} ({shop.rating_count} avis)
                </span>
              )}
              <span>{shop.product_count} produit{shop.product_count > 1 ? "s" : ""}</span>
              <span className="inline-flex items-center gap-1" data-testid="shop-views-3m">
                <Eye className="h-3 w-3" /> {shop.views_3m || 0} vues sur 3 mois
              </span>
            </div>
          </div>
        </div>

        {shop.location_detail && (
          <div className="mt-3 inline-flex items-start gap-2 p-3 rounded-xl bg-[#FFF8F3] dark:bg-[#2A1F17] border border-[#E65100]/20 text-sm" data-testid="shop-location-detail">
            <MapPin className="h-4 w-4 text-[#E65100] shrink-0 mt-0.5" />
            <span className="text-[#1A1918]">{shop.location_detail}</span>
          </div>
        )}

        {shop.description && <div className="mt-4 text-sm text-[#1A1918] leading-relaxed">{shop.description}</div>}

        <div className="mt-4 flex gap-2">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onWhatsApp}
            data-testid="shop-whatsapp-btn"
            className="flex-1 h-11 rounded-xl bg-[#0A7D3A] hover:bg-[#086a31] inline-flex items-center justify-center text-white font-medium px-3"
          >
            <MessageCircle className="h-5 w-5 mr-1" /> Contacter via WhatsApp
          </a>
          <Button
            onClick={toggleFav}
            variant="outline"
            className={`h-11 rounded-xl ${isFav ? "border-[#E65100] text-[#E65100]" : ""}`}
            data-testid="shop-favorite-btn"
            aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
          >
            <Heart className={`h-5 w-5 ${isFav ? "fill-[#E65100] text-[#E65100]" : ""}`} />
          </Button>
          <Button onClick={onShare} variant="outline" className="h-11 rounded-xl" data-testid="shop-share-btn">
            <Share2 className="h-5 w-5" />
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <TrustBadge label="Profil complété" active={shop.verification_level >= 1} />
          <TrustBadge label="Contact vérifié" active={shop.verification_level >= 2} />
          <TrustBadge label="Boutique vérifiée" active={shop.verification_level >= 3} />
        </div>

        {hasDeliveryInfo(shop) && (
          <div className="mt-5 rounded-2xl border border-[#E8E6E1] bg-white p-4" data-testid="shop-delivery-info">
            <div className="flex items-center gap-2 font-[Manrope] font-bold text-[#1A1918]">
              <Truck className="h-4 w-4 text-[#E65100]" /> Livraison & retrait
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {shop.delivery_mode && <DeliveryRow icon={Truck} label="Mode" value={deliveryModeLabel(shop.delivery_mode)} />}
              {shop.delivery_zones && <DeliveryRow icon={MapPin} label="Zones desservies" value={shop.delivery_zones} />}
              {shop.delivery_fee != null && <DeliveryRow icon={Package} label="Tarif" value={shop.delivery_fee > 0 ? formatFCFA(shop.delivery_fee) : "Gratuite"} />}
              {shop.free_delivery_from != null && shop.free_delivery_from > 0 && <DeliveryRow icon={Package} label="Livraison gratuite dès" value={formatFCFA(shop.free_delivery_from)} />}
              {shop.delivery_eta && <DeliveryRow icon={Clock} label="Délai estimé" value={shop.delivery_eta} />}
              {shop.pickup_available && <DeliveryRow icon={Store} label="Retrait sur place" value="Disponible" />}
            </div>
            {shop.delivery_notes && (
              <div className="mt-3 text-sm text-[#73706B] whitespace-pre-line border-t border-[#E8E6E1] pt-3">{shop.delivery_notes}</div>
            )}
          </div>
        )}

        <h2 className="font-[Manrope] font-bold text-lg mt-8 mb-3">Produits ({products.length})</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {products.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
        {products.length === 0 && <div className="text-center py-6 text-sm text-[#73706B]">Aucun produit pour l'instant</div>}

        {reviews.length > 0 && (
          <>
            <h2 className="font-[Manrope] font-bold text-lg mt-10 mb-3">Avis ({reviews.length})</h2>
            <div className="space-y-3">
              {reviews.map(r => (
                <div key={r.id} className="p-3 rounded-xl border border-[#E8E6E1] bg-white">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-sm">{r.user_name || "Client"}</div>
                    <div className="flex items-center gap-0.5 text-[#E65100]">
                      {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3 w-3 fill-current" />)}
                    </div>
                  </div>
                  {r.comment && <div className="mt-1 text-sm text-[#1A1918]">{r.comment}</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TrustBadge({ label, active }) {
  return (
    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${active ? "bg-[#E6F4EA] text-[#0A7D3A] border-[#0A7D3A]/20" : "bg-[#F3F1EC] text-[#73706B] border-transparent"}`}>
      {active ? "✓ " : ""}{label}
    </span>
  );
}

function DeliveryRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-[#73706B] shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#73706B]">{label}</div>
        <div className="text-[#1A1918] font-semibold truncate">{value}</div>
      </div>
    </div>
  );
}

function deliveryModeLabel(mode) {
  return {
    livraison: "Livraison uniquement",
    retrait: "Retrait uniquement",
    livraison_retrait: "Livraison + Retrait",
    aucune: "Aucune livraison",
  }[mode] || mode;
}

function hasDeliveryInfo(shop) {
  return shop.delivery_mode || shop.delivery_zones || shop.delivery_fee != null ||
    shop.free_delivery_from != null || shop.delivery_eta || shop.pickup_available || shop.delivery_notes;
}
